import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {  toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TenantAdminLayout from '../Layout/TenantAdminLayout';
import { useAcademicData } from './hooks/useAcademicData';
import AcademicSelector from './AcademicSelector';
import { useDepartments } from './hooks/useDepartments';
import CreateClassForm from './components/CreateClassForm';
import ClassList from './components/ClassList';
// ClassDetailsModal is no longer needed - we navigate to a page instead
// import ClassDetailsModal from './modals/ClassDetailsModal';
import FacultyAssignmentModal from './modals/FacultyAssignmentModal';
import LabBatchManagementModal from './modals/LabBatchManagementModal';
import ElectiveStudentsModal from './modals/ElectiveStudentsModal';
import { useClassManagerSubjects } from './hooks/useClassManagerSubjects';
import EditClassModal from './modals/EditClassModal';
import CopyClassesModal from './modals/CopyClassesModal';
import api from '../../api';

const ManageClasses = ({ 
  tenantId: propTenantId, 
  userId: propUserId, 
  organizationId: propOrgId,
  userRole: propUserRole,
  hodDepartmentId: propHodDepartmentId,
  isHODView = false 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use props or location state or localStorage
  const tenantId = propTenantId || location.state?.tenantId || localStorage.getItem('tenantId');
  const userId = propUserId || location.state?.userId || localStorage.getItem('userId');
  const organizationId = propOrgId || location.state?.organizationId || localStorage.getItem('organizationId');
  const token = localStorage.getItem('token');
  
  // ✅ Get userRole and hodDepartmentId from props or location state
  const userRole = propUserRole || location.state?.userRole || localStorage.getItem('userRole');
  const hodDepartmentId = propHodDepartmentId || location.state?.hodDepartmentId || null;
  
  // ✅ Check if user is HOD (for filtering only, not for restricting operations)
  const isHOD = userRole === 'hod' || isHODView;

  const cacheKey = useMemo(
    () => `classManagerContext:${tenantId || 'default'}`,
    [tenantId]
  );

  const cachedState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('Failed to parse class manager cached context:', err);
      return null;
    }
  }, [cacheKey]);

  const skipInitialContextFetchRef = useRef(Boolean(cachedState?.academicContext?.academicYearId && cachedState?.localClasses?.length));

  // Tab state
  const [activeTab, setActiveTab] = useState(cachedState?.activeTab || 'active');

  // State for storing terms and semesters data
  const [currentTerms, setCurrentTerms] = useState(cachedState?.currentTerms || []);
  const [currentSemesters, setCurrentSemesters] = useState(cachedState?.currentSemesters || []);
  const [currentCompletedTerms, setCurrentCompletedTerms] = useState(cachedState?.currentCompletedTerms || []);
  const [currentCompletedSemesters, setCurrentCompletedSemesters] = useState(cachedState?.currentCompletedSemesters || []);

  // Loading states for terms/semesters
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingSemesters, setLoadingSemesters] = useState(false);

  // 🔥 FIX: Add a ref to track if system type is being detected
  const [isDetectingSystem, setIsDetectingSystem] = useState(false);

  const [academicContext, setAcademicContext] = useState(() => {
    if (cachedState?.academicContext) return cachedState.academicContext;
    return {
      programTypeId: '',
      programId: '',
      academicYearId: '',
      semesterId: '',
      termId: '',
      isSemesterBased: false,
      hasExistingMapping: false,
      programMapping: null,
      systemType: null // 'semester' or 'term'
    };
  });

  // State for programs and program types
  const [allPrograms, setAllPrograms] = useState([]);
  const [allProgramTypes, setAllProgramTypes] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);

  // State for Create Class Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalContext, setCreateModalContext] = useState(null);

  // State for Copy Classes Modal
  const [showCopyModal, setShowCopyModal] = useState(false);

  // State for local classes fetching
  const [localClasses, setLocalClasses] = useState(cachedState?.localClasses || []);
  const [loadingLocalClasses, setLoadingLocalClasses] = useState(false);

  // Fetch academic years, semesters, and classes data
  const {
    academicYears,
    completedAcademicYears,
    semesters,
    completedSemesters,
    terms,
    completedTerms,
    fetchClasses,
    loading: classesLoading,
    isViewOnly,
  } = useAcademicData(tenantId, token, true);

  const { departments, loading: departmentsLoading } = useDepartments(tenantId, !!academicContext.academicYearId);

  // Update terms and semesters when props change
  useEffect(() => {
    if (activeTab === 'active') {
      setCurrentSemesters(semesters || []);
      setCurrentTerms(terms || []);
    } else {
      setCurrentCompletedSemesters(completedSemesters || []);
      setCurrentCompletedTerms(completedTerms || []);
    }
  }, [activeTab, semesters, completedSemesters, terms, completedTerms]);

  // Get academic year and semester/term data based on context
  const selectedAcademicYearData = useMemo(() => {
    const allYears = activeTab === 'active' ? academicYears : completedAcademicYears;
    return allYears.find(ay => ay._id === academicContext.academicYearId);
  }, [academicYears, completedAcademicYears, activeTab, academicContext.academicYearId]);

  const selectedSemesterData = useMemo(() => {
    const allSemesters = activeTab === 'active' ? currentSemesters : currentCompletedSemesters;
    return allSemesters.find(s => s._id === academicContext.semesterId);
  }, [currentSemesters, currentCompletedSemesters, activeTab, academicContext.semesterId]);

  const selectedTermData = useMemo(() => {
    const allTerms = activeTab === 'active' ? currentTerms : currentCompletedTerms;
    return allTerms.find(t => t._id === academicContext.termId || t.id === academicContext.termId);
  }, [currentTerms, currentCompletedTerms, activeTab, academicContext.termId]);

  // Fetch all programs for the tenant
  useEffect(() => {
    const fetchAllPrograms = async () => {
      if (!tenantId) return;
      
      try {
        setLoadingPrograms(true);
        const response = await api.get('/api/programs', {
          params: { tenantId, status: 'active' },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setAllPrograms(response.data.programs || []);
      } catch (err) {
        console.error('Error fetching programs:', err);
        toast.error('Failed to load programs');
      } finally {
        setLoadingPrograms(false);
      }
    };

    const fetchAllProgramTypes = async () => {
      if (!tenantId) return;
      
      try {
        const response = await api.get('/api/program-types', {
          params: { tenantId, status: 'active' },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setAllProgramTypes(response.data.programTypes || []);
      } catch (err) {
        console.error('Error fetching program types:', err);
      }
    };

    fetchAllPrograms();
    fetchAllProgramTypes();
  }, [tenantId, token]);

  // Fetch terms for a specific academic year
  const fetchTermsForYear = useCallback(async (academicYearId) => {
    if (!academicYearId || !tenantId) return [];
    
    try {
      setLoadingTerms(true);
      setIsDetectingSystem(true);
      console.log('📅 Fetching terms for year:', academicYearId);
      
      const response = await api.get(`/api/academic-year/${academicYearId}/terms`, {
        params: { tenantId }
      });
      
      const termsList = response.data.terms || [];
      console.log(`✅ Found ${termsList.length} terms`);
      
      return termsList;
    } catch (err) {
      console.error('❌ Error fetching terms:', err);
      return [];
    } finally {
      setLoadingTerms(false);
      setIsDetectingSystem(false);
    }
  }, [tenantId]);

  // Fetch semesters for a specific academic year
  const fetchSemestersForYear = useCallback(async (academicYearId) => {
    if (!academicYearId) return [];
    
    try {
      setLoadingSemesters(true);
      setIsDetectingSystem(true);
      console.log('📚 Fetching semesters for year:', academicYearId);
      
      const response = await api.get(`/api/classManager/semesters/${academicYearId}`);
      
      const semestersList = response.data.semesters || [];
      console.log(`✅ Found ${semestersList.length} semesters`);
      
      return semestersList;
    } catch (err) {
      console.error('❌ Error fetching semesters:', err);
      return [];
    } finally {
      setLoadingSemesters(false);
      setIsDetectingSystem(false);
    }
  }, []);

  // Fetch local classes based on academic context
  const fetchLocalClasses = useCallback(async () => {
    if (!academicContext.academicYearId || !academicContext.programId) {
      setLocalClasses([]);
      return;
    }
    
    try {
      setLoadingLocalClasses(true);
      
      let response;
      const params = {
        programTypeId: academicContext.programTypeId,
        programId: academicContext.programId
      };
      
      console.log('🔍 Fetching classes with context:', academicContext);
      
      if (academicContext.isSemesterBased && academicContext.semesterId) {
        console.log('📚 Fetching classes for semester:', academicContext.semesterId);
        response = await api.get(`/api/classManager/classes-for-semester/${academicContext.semesterId}`, {
          params,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } else if (!academicContext.isSemesterBased && academicContext.termId) {
        console.log('📅 Fetching classes for term:', academicContext.termId);
        response = await api.get(`/api/classManager/classes-for-term/${academicContext.termId}`, {
          params,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      } else {
        console.log('📋 Fetching all classes for academic year:', academicContext.academicYearId);
        response = await api.get(`/api/classManager/classes-for-academic-year/${academicContext.academicYearId}`, {
          params: {
            ...params,
            isSemesterBased: academicContext.isSemesterBased ? 'true' : 'false'
          },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      
      let fetchedClasses = response.data?.classes || response.data || [];
      
      // ✅ HOD FILTERING: Only show classes where department matches HOD's department
      if (isHOD && hodDepartmentId && fetchedClasses.length > 0) {
        console.log(`🔍 [HOD] Filtering classes for department: ${hodDepartmentId}`);
        fetchedClasses = fetchedClasses.filter(classItem => {
          // Check if class belongs to HOD's department
          const classDeptId = classItem.departmentId?._id || classItem.departmentId || '';
          const matchesDept = String(classDeptId) === String(hodDepartmentId);
          
          // Also check departmentIds array
          let matchesDeptIds = false;
          if (classItem.departmentIds && Array.isArray(classItem.departmentIds)) {
            matchesDeptIds = classItem.departmentIds.some(id => 
              String(id) === String(hodDepartmentId)
            );
          }
          
          // Also check departmentSubjects
          let matchesDeptSubjects = false;
          if (classItem.departmentSubjects && Array.isArray(classItem.departmentSubjects)) {
            matchesDeptSubjects = classItem.departmentSubjects.some(deptSubj => 
              String(deptSubj.departmentId) === String(hodDepartmentId)
            );
          }
          
          return matchesDept || matchesDeptIds || matchesDeptSubjects;
        });
        console.log(`✅ [HOD] Filtered to ${fetchedClasses.length} classes for department`);
      }
      
      console.log(`✅ Found ${fetchedClasses.length} classes`);
      
      setLocalClasses(Array.isArray(fetchedClasses) ? fetchedClasses : []);
    } catch (err) {
      console.error('❌ Error fetching local classes:', err);
      toast.error('Failed to load classes');
      setLocalClasses([]);
    } finally {
      setLoadingLocalClasses(false);
    }
  }, [academicContext, token, isHOD, hodDepartmentId]);

  // Fetch local classes when context changes
  useEffect(() => {
    if (academicContext.academicYearId && academicContext.programId && academicContext.systemType) {
      if (skipInitialContextFetchRef.current) {
        skipInitialContextFetchRef.current = false;
        return;
      }
      fetchLocalClasses();
    } else {
      if (!skipInitialContextFetchRef.current) {
        setLocalClasses([]);
      }
    }
  }, [academicContext, fetchLocalClasses]);

  useEffect(() => {
    try {
      const snapshot = {
        activeTab,
        academicContext,
        localClasses,
        currentTerms,
        currentSemesters,
        currentCompletedTerms,
        currentCompletedSemesters
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(snapshot));
    } catch (err) {
      console.warn('Failed to persist class manager context:', err);
    }
  }, [
    cacheKey,
    activeTab,
    academicContext,
    localClasses,
    currentTerms,
    currentSemesters,
    currentCompletedTerms,
    currentCompletedSemesters
  ]);

  // Fetch subjects for the selected context
  const { 
    subjects: subjectsData, 
    loading: subjectsLoading,
  } = useClassManagerSubjects({
    tenantId,
    academicYearId: academicContext.academicYearId,
    semesterId: academicContext.isSemesterBased ? academicContext.semesterId : null,
    termId: !academicContext.isSemesterBased ? academicContext.termId : null,
    departmentId: null,
    isSemesterBased: academicContext.isSemesterBased
  });

  // Modal states - removed isDetailsOpen and selectedClassId since we navigate to a page now
  const [activeModal, setActiveModal] = useState(null);
  const [, setModalProps] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filter classes based on academic context
  const filteredClasses = useMemo(() => {
    return localClasses;
  }, [localClasses]);

  const allSubjects = useMemo(() => {
    if (!subjectsData?.all) return [];
    return Array.isArray(subjectsData.all) ? subjectsData.all : [];
  }, [subjectsData]);

  const isLoading = classesLoading || departmentsLoading || subjectsLoading || loadingPrograms || loadingLocalClasses || loadingTerms || loadingSemesters;

  // Status info
  const getStatusInfo = useMemo(() => {
    if (activeTab === 'completed') {
      return {
        text: 'Completed',
        type: 'completed',
        badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
        icon: '📚',
        isHistorical: true
      };
    }
    
    if (academicContext.isSemesterBased && selectedSemesterData?.status === 'completed') {
      return {
        text: 'Completed Semester',
        type: 'completed-semester',
        badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: '📖',
        isHistorical: true
      };
    }
    
    if (!academicContext.isSemesterBased && selectedTermData?.status === 'completed') {
      return {
        text: 'Completed Term',
        type: 'completed-term',
        badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: '📖',
        isHistorical: true
      };
    }
    
    return {
      text: 'Active',
      type: 'active',
      badgeClass: 'bg-green-50 text-green-700 border-green-200',
      icon: '⚡',
      isHistorical: false
    };
  }, [activeTab, academicContext.isSemesterBased, selectedSemesterData, selectedTermData]);

  // Toast notification helper
  const notify = useCallback((message, type = 'info') => {
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      default:
        toast.info(message);
    }
  }, []);

  // 🔥 FIX: Enhanced context change handler with better state management
  const handleContextChange = useCallback(async (context) => {
    console.log('[ManageClasses] Context changed:', context);
    
    // Immediately update basic context without system type
    setAcademicContext(prev => ({
      ...prev,
      programTypeId: context.programTypeId || '',
      programId: context.programId || '',
      academicYearId: context.academicYearId || '',
      semesterId: context.semesterId || '',
      termId: context.termId || '',
      hasExistingMapping: context.hasExistingMapping || false,
      programMapping: context.programMapping || null
    }));
    
    // Store the terms and semesters data
    if (context.terms) {
      console.log('[ManageClasses] Setting terms:', context.terms);
      setCurrentTerms(context.terms);
    }
    if (context.semesters) {
      console.log('[ManageClasses] Setting semesters:', context.semesters);
      setCurrentSemesters(context.semesters);
    }
    if (context.completedTerms) {
      setCurrentCompletedTerms(context.completedTerms);
    }
    if (context.completedSemesters) {
      setCurrentCompletedSemesters(context.completedSemesters);
    }
    
    // If we have a system type from AcademicSelector, use it immediately
    if (context.systemType) {
      console.log(`[ManageClasses] System type received: ${context.systemType}`);
      setAcademicContext(prev => ({
        ...prev,
        isSemesterBased: context.systemType === 'semester',
        systemType: context.systemType,
        semesterId: context.systemType === 'semester' ? context.semesterId || '' : '',
        termId: context.systemType === 'term' ? context.termId || '' : ''
      }));
      return;
    }
    
    // If we have an academic year but no system type, detect it
    if (context.academicYearId && !context.systemType) {
      console.log('🔍 System type not provided, detecting directly...');
      setIsDetectingSystem(true);
      
      // Try to fetch both
      const [termsData, semestersData] = await Promise.all([
        fetchTermsForYear(context.academicYearId),
        fetchSemestersForYear(context.academicYearId)
      ]);
      
      let detectedSystemType = null;
      
      if (semestersData.length > 0) {
        detectedSystemType = 'semester';
        setCurrentSemesters(semestersData);
        console.log('✅ Detected semester-based system');
      } else if (termsData.length > 0) {
        detectedSystemType = 'term';
        setCurrentTerms(termsData);
        console.log('✅ Detected term-based system');
      }
      
      if (detectedSystemType) {
        setAcademicContext(prev => ({
          ...prev,
          isSemesterBased: detectedSystemType === 'semester',
          systemType: detectedSystemType
        }));
      }
      
      setIsDetectingSystem(false);
    }
  }, [fetchTermsForYear, fetchSemestersForYear]);

  // Handle mapping check
  const handleMappingCheck = useCallback((mapping) => {
    // Mapping check handled by AcademicSelector
  }, []);

  // Handle class created
  const handleClassCreated = useCallback(() => {
    setShowCreateModal(false);
    setCreateModalContext(null);
    fetchClasses();
    fetchLocalClasses();
    notify('Class created successfully!', 'success');
  }, [fetchClasses, fetchLocalClasses, notify]);

  // Handle class updated
  const handleClassUpdated = useCallback(() => {
    fetchClasses();
    fetchLocalClasses();
    notify('Class updated successfully!', 'success');
  }, [fetchClasses, fetchLocalClasses, notify]);

  // Detect if viewing completed academic year
  const isViewingCompletedAcademicYear = useMemo(() => {
    if (!academicContext.academicYearId) return false;
    return completedAcademicYears.some(ay => ay._id === academicContext.academicYearId);
  }, [academicContext.academicYearId, completedAcademicYears]);

  // Sync activeTab with the actual data
  useEffect(() => {
    if (isViewingCompletedAcademicYear && activeTab !== 'completed') {
      setActiveTab('completed');
    } else if (!isViewingCompletedAcademicYear && activeTab === 'completed') {
      setActiveTab('active');
    }
  }, [isViewingCompletedAcademicYear, activeTab]);

  // Enhanced isViewOnly logic
  const isEffectivelyViewOnly = useMemo(() => {
    return isViewOnly || 
           activeTab === 'completed' ||
           isViewingCompletedAcademicYear ||
           (academicContext.isSemesterBased && selectedSemesterData?.status === 'completed') ||
           (!academicContext.isSemesterBased && selectedTermData?.status === 'completed') ||
           selectedAcademicYearData?.status === 'completed';
  }, [isViewOnly, activeTab, isViewingCompletedAcademicYear, academicContext.isSemesterBased, selectedSemesterData, selectedTermData, selectedAcademicYearData]);

  // 🔥 FIX: Handle create class click with better validation
  const handleCreateClassClick = useCallback((context) => {
    console.log('[ManageClasses] Create class clicked with context:', context);
    
    if (isEffectivelyViewOnly) {
      notify('Cannot create classes for completed academic year/semester/term', 'warning');
      return;
    }
    
    if (!context.academicYearId || !context.programId) {
      notify('Please select program and academic year first', 'warning');
      return;
    }

    // Check if we're still detecting
    if (isDetectingSystem) {
      notify('System type detection in progress. Please wait a moment...', 'info');
      return;
    }

    // Use the system type from the context if available
    const systemType = context.systemType || academicContext.systemType;
    
    if (!systemType) {
      console.log('❌ No system type available', { context, academicContext });
      notify('Please wait for system type detection to complete', 'warning');
      return;
    }

    const selectedProgramType = allProgramTypes.find(pt => pt._id === context.programTypeId);
    const selectedProgram = allPrograms.find(p => p._id === context.programId);

    const modalContext = {
      tenantId,
      programId: context.programId,
      programTypeId: context.programTypeId,
      academicYearId: context.academicYearId,
      ...(systemType === 'semester' 
        ? { semesterId: context.semesterId || academicContext.semesterId } 
        : { termId: context.termId || academicContext.termId }
      ),
      departmentId: null,
      mappingData: context.programMapping || academicContext.programMapping,
      isSemesterBased: systemType === 'semester',
      selectedProgramName: selectedProgram?.name || '',
      selectedProgramTypeName: selectedProgramType?.name || '',
      systemType
    };
    
    console.log('[ManageClasses] Setting create modal context:', modalContext);
    setCreateModalContext(modalContext);
    setShowCreateModal(true);
  }, [isEffectivelyViewOnly, tenantId, allPrograms, allProgramTypes, notify, academicContext, isDetectingSystem]);

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateModalContext(null);
  };

// In ManageClasses.js - Update handleViewDetails function

const handleViewDetails = (_viewType, { classId }) => {
  // Get all necessary data from the current context
  const navigationState = {
    classId: classId,
    classes: filteredClasses,
    departments: departments || [],
    subjects: allSubjects,
    allPrograms: allPrograms,
    allProgramTypes: allProgramTypes,
    academicYearStatus: activeTab,
    isViewOnly: isEffectivelyViewOnly,
    tenantId: tenantId,
    userId: userId,
    organizationId: organizationId,
    activeTab: activeTab,
    currentSemesters: currentSemesters,
    currentCompletedSemesters: currentCompletedSemesters,
    currentTerms: currentTerms,
    currentCompletedTerms: currentCompletedTerms,
    semesters: semesters,
    completedSemesters: completedSemesters,
    terms: terms,
    completedTerms: completedTerms,
    filteredClasses: filteredClasses,
    academicContext: {
      programTypeId: academicContext.programTypeId,
      programId: academicContext.programId,
      academicYearId: academicContext.academicYearId,
      semesterId: academicContext.semesterId,
      termId: academicContext.termId,
      isSemesterBased: academicContext.isSemesterBased,
      systemType: academicContext.systemType,
      programMapping: academicContext.programMapping,
      hasExistingMapping: academicContext.hasExistingMapping
    }
  };

  // ✅ Navigate based on user role
  if (isHOD) {
    navigate('/hod/class-details', { state: navigationState });
  } else {
    navigate('/tenant-admin/class-details', { state: navigationState });
  }
};

  const handleEditClass = (_action, { classId }) => {
    if (isEffectivelyViewOnly) {
      notify('Cannot edit classes for completed academic year/semester/term', 'warning');
      return;
    }
    
    const cls = filteredClasses.find(c => String(c._id) === String(classId));
    if (!cls) {
      notify('Class not found for editing', 'error');
      return;
    }

    setActiveModal({
      type: 'editClass',
      props: {
        classId: cls._id,
        className: cls.className,
        classObj: cls,
        isViewOnly: isEffectivelyViewOnly,
        isSemesterBased: cls.isSemesterBased,
        semesterId: cls.semesterId,
        termId: cls.termId
      }
    });
  };

  const handleDeleteClass = (classId) => {
    if (isEffectivelyViewOnly) {
      notify('Cannot delete classes for completed academic year/semester/term', 'warning');
      return;
    }
    
    const cls = filteredClasses.find(c => String(c._id) === String(classId));
    if (!cls) {
      notify('Class not found', 'error');
      return;
    }
    setClassToDelete(cls);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteClass = async () => {
    if (!classToDelete) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');

      await api.delete(`/api/classManager/class/${classToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      notify(`Class "${classToDelete.className}" deleted successfully`, 'success');
      setShowDeleteConfirm(false);
      setClassToDelete(null);
      await fetchClasses();
      await fetchLocalClasses();
    } catch (err) {
      console.error('Error deleting class:', err);
      notify('Failed to delete class', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setClassToDelete(null);
  };

  const handleOpenCopyModal = useCallback(() => {
    if (isEffectivelyViewOnly) {
      notify('Cannot copy classes in view-only mode', 'warning');
      return;
    }
    
    if (!academicContext.academicYearId) {
      notify('Please select an academic year first', 'warning');
      return;
    }
    
    setShowCopyModal(true);
  }, [isEffectivelyViewOnly, academicContext.academicYearId, notify]);

  const handleCloseCopyModal = useCallback(() => {
    setShowCopyModal(false);
  }, []);

  const handleCopySuccess = useCallback(async (result) => {
    await fetchClasses();
    await fetchLocalClasses();
    
    const { summary } = result;
    if (summary) {
      const message = `Successfully copied ${summary.copied} class(es). ${summary.skipped > 0 ? `Skipped: ${summary.skipped}` : ''} ${summary.errors > 0 ? `Errors: ${summary.errors}` : ''}`;
      notify(message, summary.errors > 0 ? 'warning' : 'success');
    } else {
      notify('Classes copied successfully!', 'success');
    }
    
    setShowCopyModal(false);
  }, [fetchClasses, fetchLocalClasses, notify]);

  const handleShowCopySection = useCallback(() => {
    if (isEffectivelyViewOnly) {
      notify('Cannot copy classes in view-only mode', 'warning');
      return;
    }
    setShowCopyModal(true);
  }, [isEffectivelyViewOnly, notify]);

  const handleFacultyAssignment = (classId, className, classSubjects) => {
    if (isEffectivelyViewOnly) {
      notify('Cannot modify faculty assignments for completed academic years', 'warning');
      return;
    }
    
    setActiveModal({
      type: 'facultyAssignment',
      props: { 
        classId, 
        className, 
        subjects: classSubjects,
        isViewOnly: isEffectivelyViewOnly
      }
    });
  };

  /**
   * UPDATED: Handle Lab Batch Management with academic period information
   * This ensures term-based classes pass termId to the lab batch management modal
   */
  const handleLabBatchManagement = (classData) => {
    if (isEffectivelyViewOnly) {
      notify('Cannot modify lab batches for completed academic years', 'warning');
      return;
    }
    
    // Extract class data with academic period info
    const classObj = classData.classObj || classData;
    
    // Log for debugging
    console.log('[ManageClasses] Opening lab batch management for class:', {
      classId: classObj._id,
      className: classObj.className || classObj.name,
      isSemesterBased: classObj.isSemesterBased,
      semesterId: classObj.semesterId,
      termId: classObj.termId,
      semesterNumber: classObj.semesterNumber,
      termNumber: classObj.termNumber
    });
    
    setActiveModal({
      type: 'labBatchManagement',
      props: { 
        classId: classObj._id, 
        className: classObj.className || classObj.name, 
        labSubjects: classObj.subjects || classData.labSubjects || [],
        isViewOnly: isEffectivelyViewOnly,
        // Pass academic period information for term/semester awareness
        isSemesterBased: classObj.isSemesterBased,
        semesterId: classObj.semesterId,
        termId: classObj.termId,
        semesterNumber: classObj.semesterNumber,
        termNumber: classObj.termNumber,
        // Include tenant and user info
        tenantId: tenantId,
        userId: userId,
        organizationId: organizationId
      }
    });
  };

  const openModal = (type, props = {}) => {
    setActiveModal({ type, props });
  };
   // eslint-disable-next-line 
  const handleManageElectiveStudents = (cls, electiveSubjects) => {
    if (isEffectivelyViewOnly) {
      notify('Cannot modify student assignments for completed academic years', 'warning');
      return;
    }
    
    openModal('manageElectiveStudents', {
      classId: cls._id,
      classObj: cls,
      className: cls.className,
      groupType: 'elective',
      electiveSubjects: electiveSubjects,
      electiveSubjectIds: electiveSubjects.map(s => s._id),
      isViewOnly: isEffectivelyViewOnly
    });
  };
  // eslint-disable-next-line  
  const handleManageLanguageStudents = (cls, languageSubjects) => {
    if (isEffectivelyViewOnly) {
      notify('Cannot modify student assignments for completed academic years', 'warning');
      return;
    }
    
    openModal('manageElectiveStudents', {
      classId: cls._id,
      classObj: cls,
      className: cls.className,
      groupType: 'language',
      electiveSubjects: languageSubjects,
      electiveSubjectIds: languageSubjects.map(s => s._id),
      isViewOnly: isEffectivelyViewOnly
    });
  };

  const closeModal = async () => {
    if (
      activeModal?.type === 'facultyAssignment' ||
      activeModal?.type === 'labBatchManagement' ||
      activeModal?.type === 'manageElectiveStudents' ||
      activeModal?.type === 'editClass'
    ) {
      await fetchClasses();
      await fetchLocalClasses();
    }
    setActiveModal(null);
    setModalProps({});
  };

  // Get statistics for the current context
  const getStats = useCallback(() => {
    const mappingClasses = filteredClasses.filter(c => c.usedMappingForSubjects).length;
    const labClasses = filteredClasses.filter(c => c.hasLabBatches).length;
    const semesterBasedClasses = filteredClasses.filter(c => c.isSemesterBased).length;
    const termBasedClasses = filteredClasses.filter(c => !c.isSemesterBased).length;
    
    return {
      total: filteredClasses.length,
      mappingDriven: mappingClasses,
      manual: filteredClasses.length - mappingClasses,
      withLabs: labClasses,
      semesterBased: semesterBasedClasses,
      termBased: termBasedClasses
    };
  }, [filteredClasses]);

  const stats = getStats();

  // Check if create button should be enabled
  const canCreateClass = useMemo(() => {
    return !isEffectivelyViewOnly && 
           academicContext.academicYearId && 
           academicContext.programId &&
           (academicContext.systemType !== null) &&
           !isDetectingSystem;
  }, [isEffectivelyViewOnly, academicContext, isDetectingSystem]);

  // Get system type text
  const getSystemTypeText = () => {
    if (academicContext.isSemesterBased) {
      return 'Semester System';
    }
    return 'Term System';
  };

  // Build the content
  const content = (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 lg:py-6">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 space-y-4 lg:space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/60 p-4 lg:p-6 backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-3 lg:space-x-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14v6l9-5-9-5-9 5 9 5z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {isHOD ? 'Department Classes' : 'Class Management'}
                </h1>
                <p className="text-gray-600 text-sm lg:text-base mt-1">
                  {isHOD ? 'Manage classes for your department' : 'Manage classes using program-subject mappings'}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 lg:gap-4">
              {/* ✅ Allow HOD to copy classes */}
              {!isEffectivelyViewOnly && (
                <button
                  onClick={handleOpenCopyModal}
                  disabled={!academicContext.academicYearId || isDetectingSystem}
                  className={`flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium ${
                    (!academicContext.academicYearId || isDetectingSystem) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy Classes</span>
                </button>
              )}

              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${getStatusInfo.badgeClass} shadow-sm`}>
                <span>{getStatusInfo.icon}</span>
                <span className="text-sm font-medium">{getStatusInfo.text}</span>
                <span className="text-xs text-gray-500">• {getSystemTypeText()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Academic Selector */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/60 p-4 lg:p-6 backdrop-blur-sm">
          <AcademicSelector
            tenantId={tenantId}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            academicYears={academicYears}
            completedAcademicYears={completedAcademicYears}
            selectedAcademicYear={academicContext.academicYearId}
            setSelectedAcademicYear={(value) =>
              setAcademicContext(prev => ({
                ...prev,
                academicYearId: value,
                semesterId: '',
                termId: '',
                systemType: null,
                isSemesterBased: false
              }))
            }
            isSemesterBased={academicContext.isSemesterBased}
            semesters={currentSemesters}
            completedSemesters={currentCompletedSemesters}
            terms={currentTerms}
            completedTerms={currentCompletedTerms}
            selectedSemester={academicContext.semesterId}
            setSelectedSemester={(value) => setAcademicContext(prev => ({ ...prev, semesterId: value }))}
            selectedTerm={academicContext.termId}
            setSelectedTerm={(value) => setAcademicContext(prev => ({ ...prev, termId: value }))}
            includeCompleted={true}
            onCreateClass={handleCreateClassClick}
            loading={isLoading}
            isViewOnly={isEffectivelyViewOnly} // ✅ Removed || isHOD
            compact={true}
            fetchMappings={true}
            onContextChange={handleContextChange}
            onMappingCheck={handleMappingCheck}
            showMappingStatus={true}
            enableReset={true}
            programTypes={allProgramTypes}
            programs={allPrograms}
            selectedProgramTypeId={academicContext.programTypeId}
            setSelectedProgramTypeId={(value) => setAcademicContext(prev => ({ ...prev, programTypeId: value, systemType: null }))}
            selectedProgramId={academicContext.programId}
            setSelectedProgramId={(value) => setAcademicContext(prev => ({ ...prev, programId: value, systemType: null }))}
          />
        </div>

        {/* Stats Cards */}
        {academicContext.academicYearId && academicContext.programId && academicContext.systemType && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500">Mapping-Driven</div>
              <div className="text-xl font-bold text-green-600">{stats.mappingDriven}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500">Manual</div>
              <div className="text-xl font-bold text-blue-600">{stats.manual}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500">With Labs</div>
              <div className="text-xl font-bold text-purple-600">{stats.withLabs}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500">Semester</div>
              <div className="text-xl font-bold text-indigo-600">{stats.semesterBased}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-xs text-gray-500">Term</div>
              <div className="text-xl font-bold text-orange-600">{stats.termBased}</div>
            </div>
          </div>
        )}

        {/* Class List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/60 overflow-hidden backdrop-blur-sm">
          {academicContext.academicYearId && academicContext.programId && academicContext.systemType ? (
            <ClassList
              key={`class-list-${activeTab}-${academicContext.academicYearId}-${academicContext.semesterId}-${academicContext.termId}`}
              classes={filteredClasses}
              departments={departments || []}
              subjects={subjectsData || {}}
              onViewDetails={handleViewDetails}
              onEdit={handleEditClass}
              onDelete={handleDeleteClass}
              isSemesterBased={academicContext.isSemesterBased}
              semesters={academicContext.isSemesterBased ? 
                (activeTab === 'active' ? currentSemesters : currentCompletedSemesters) : 
                []
              }
              terms={!academicContext.isSemesterBased ?
                (activeTab === 'active' ? currentTerms : currentCompletedTerms) :
                []
              }
              onShowCopySection={handleShowCopySection}
              onFacultyAssignment={handleFacultyAssignment}
              onLabBatchManagement={handleLabBatchManagement}
              loading={isLoading}
              academicYearStatus={activeTab}
              isViewOnly={isEffectivelyViewOnly} // ✅ Removed || isHOD
              selectedSemester={academicContext.semesterId}
              selectedSemesterData={selectedSemesterData}
              selectedTerm={academicContext.termId}
              selectedTermData={selectedTermData}
              compact={true}
              showCreateButton={canCreateClass}
              onCreateClick={() => handleCreateClassClick(academicContext)}
              mappingUsed={academicContext.hasExistingMapping}
              allPrograms={allPrograms}
              allProgramTypes={allProgramTypes}
              hideDeleteButton={isEffectivelyViewOnly} // ✅ Removed || isHOD
              hideEditButton={isEffectivelyViewOnly} // ✅ Removed || isHOD
              selectedProgramTypeId={academicContext.programTypeId}
              selectedProgramId={academicContext.programId}
              selectedAcademicYearId={academicContext.academicYearId}
              selectedSemesterId={academicContext.semesterId}
              selectedTermId={academicContext.termId}
            />
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-3">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14v6l9-5-9-5-9 5 9 5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Select Program and Academic Year</h3>
              <p className="text-gray-600 mb-4">
                Please select a program and academic year from the selector above to view classes.
              </p>
              {academicContext.academicYearId && !academicContext.systemType && (
                <p className="text-sm text-blue-600 animate-pulse">
                  Detecting system type (semester/term)...
                </p>
              )}
              {isDetectingSystem && (
                <p className="text-sm text-purple-600 animate-pulse mt-2">
                  Loading semester/term data...
                </p>
              )}
            </div>
          )}
        </div>

        {/* Modals - ✅ Removed !isHOD conditions */}
        {showCreateModal && createModalContext && !isEffectivelyViewOnly && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 lg:p-4 z-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl lg:max-w-6xl max-h-[90vh] lg:max-h-[95vh] overflow-hidden">
              <CreateClassForm
                tenantId={tenantId}
                userId={userId}
                selectedAcademicYear={createModalContext.academicYearId}
                semesterId={createModalContext.semesterId}
                termId={createModalContext.termId}
                isSemesterBased={createModalContext.isSemesterBased}
                academicYears={activeTab === 'active' ? academicYears : completedAcademicYears}
                semesters={createModalContext.isSemesterBased ? 
                  (activeTab === 'active' ? currentSemesters : currentCompletedSemesters) : 
                  []
                }
                terms={!createModalContext.isSemesterBased ?
                  (activeTab === 'active' ? currentTerms : currentCompletedTerms) :
                  []
                }
                departments={departments}
                programs={allPrograms}
                programTypes={allProgramTypes}
                closeModal={handleCloseCreateModal}
                onClassCreated={handleClassCreated}
                notify={notify}
                isEditMode={false}
                classToEdit={null}
                mappingData={createModalContext.mappingData}
                programId={createModalContext.programId}
                programTypeId={createModalContext.programTypeId}
                programName={createModalContext.selectedProgramName}
                programTypeName={createModalContext.selectedProgramTypeName}
                isLoading={isLoading}
                key={`create-form-${createModalContext.termId}-${currentTerms.length}`}
              />
            </div>
          </div>
        )}

        {/* ClassDetailsModal removed - now using page navigation */}

        {activeModal?.type === 'facultyAssignment' && (
          <FacultyAssignmentModal
            isOpen={true}
            onClose={closeModal}
            classId={activeModal.props.classId}
            className={activeModal.props.className}
            subjects={activeModal.props.subjects || []}
            notify={notify}
            isViewOnly={activeModal.props.isViewOnly || isEffectivelyViewOnly}
          />
        )}

        {activeModal?.type === 'labBatchManagement' && (
          <LabBatchManagementModal
            isOpen={true}
            onClose={closeModal}
            classId={activeModal.props.classId}
            className={activeModal.props.className}
            labSubjects={activeModal.props.labSubjects || []}
            notify={notify}
            isViewOnly={activeModal.props.isViewOnly || isEffectivelyViewOnly}
            tenantId={tenantId}
            userId={userId}
            organizationId={organizationId}
            // Pass academic period information for term/semester awareness
            isSemesterBased={activeModal.props.isSemesterBased}
            semesterId={activeModal.props.semesterId}
            termId={activeModal.props.termId}
            semesterNumber={activeModal.props.semesterNumber}
            termNumber={activeModal.props.termNumber}
          />
        )}

        {activeModal?.type === 'editClass' && !isEffectivelyViewOnly && (
          <EditClassModal
            isOpen={true}
            onClose={closeModal}
            classId={activeModal.props.classId}
            className={activeModal.props.className}
            tenantId={tenantId}
            academicYears={activeTab === 'active' ? academicYears : completedAcademicYears}
            semesters={activeModal.props.isSemesterBased ? 
              (activeTab === 'active' ? semesters : completedSemesters) : 
              []
            }
            terms={!activeModal.props.isSemesterBased ?
              (activeTab === 'active' ? terms : completedTerms) :
              []
            }
            departments={departments}
            programs={allPrograms}
            programTypes={allProgramTypes}
            notify={notify}
            onClassUpdated={handleClassUpdated}
            isViewOnly={isEffectivelyViewOnly}
            mappingData={academicContext.programMapping}
          />
        )}

        {activeModal?.type === 'manageElectiveStudents' && (
          <ElectiveStudentsModal
            isOpen={true}
            onClose={closeModal}
            classId={activeModal.props.classId}
            classObj={activeModal.props.classObj}
            className={activeModal.props.className}
            groupType={activeModal.props.groupType}
            electiveSubjects={activeModal.props.electiveSubjects || []}
            electiveSubjectIds={activeModal.props.electiveSubjectIds || []}
            notify={notify}
            isViewOnly={activeModal.props.isViewOnly || isEffectivelyViewOnly}
          />
        )}

        {showDeleteConfirm && classToDelete && !isEffectivelyViewOnly && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Class</h3>
                  <p className="text-sm text-gray-600 mt-1">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">
                  Are you sure you want to delete the class{' '}
                  <span className="font-semibold">{classToDelete.className}</span>?
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteClass}
                  disabled={deleting}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCopyModal && !isEffectivelyViewOnly && (
          <CopyClassesModal
            isOpen={showCopyModal}
            onClose={handleCloseCopyModal}
            tenantId={tenantId}
            academicYears={[...academicYears, ...completedAcademicYears]}
            currentAcademicYearId={academicContext.academicYearId}
            currentSemesterId={academicContext.semesterId}
            currentTermId={academicContext.termId}
            isSemesterBased={academicContext.isSemesterBased}
            onCopySuccess={handleCopySuccess}
            notify={notify}
            createdBy={userId}
          />
        )}
      </div>
    </div>
  );

  // If HOD view, render without TenantAdminLayout
  if (isHOD) {
    return content;
  }

  // For non-HOD users, wrap with TenantAdminLayout
  return (
    <TenantAdminLayout>
      {content}
    </TenantAdminLayout>
  );
};

export default ManageClasses;